import { definePreset } from "@primeuix/themes";
import Aura from "@primevue/themes/aura";

/**
 * persolog corporate identity as a PrimeVue preset (on top of Aura).
 * Primary = persolog orange (#FC561D), secondary = persolog blue (#0E3772).
 * Shared across all persolog apps so the brand colors stay identical.
 */
/**
 * Square accordion corners, per the persolog CI guide.
 *
 * Aura already sets `header.borderRadius: 0`, but still rounds the *first* panel's top and the *last*
 * panel's bottom from `content.border.radius` — which is where the stray rounding came from. Zeroing
 * those two here fixes it for every accordion in every app, instead of each view repeating a
 * `border-radius: 0 !important` override.
 */
const SQUARE_ACCORDION = {
    header: {
        first: { topBorderRadius: "0" },
        last: { bottomBorderRadius: "0", activeBottomBorderRadius: "0" },
    },
};

/**
 * Square fieldset corners, per the persolog CI guide. Aura leaves the *legend* rounded even though
 * the box itself is square, so the legend chip floated with 6px corners (RDT-339); zeroing both
 * fixes every fieldset in every app rather than each view overriding it.
 */
const SQUARE_FIELDSET = {
    borderRadius: "0",
    legend: { borderRadius: "0" },
};

export const persologDefault = definePreset(Aura, {
    primitive: {
        orange: {
            50: "#FDE2D4",
            100: "#FCD0B8",
            200: "#FCB99A",
            300: "#FCA37C",
            400: "#FC8D5E",
            500: "#FC561D",
            600: "#E64E1B",
            700: "#CF4619",
            800: "#B93E17",
            900: "#A23615",
            950: "#8B2F13",
        },
        blue: {
            50: "#E6E9F3",
            100: "#CCD2E6",
            200: "#B2BCDA",
            300: "#98A5CD",
            400: "#7E8FC1",
            500: "#0E3772",
            600: "#0C3165",
            700: "#0A2B58",
            800: "#08254B",
            900: "#061F3E",
            950: "#041932",
        },
    },
    semantic: {
        primary: {
            50: "{orange.50}",
            100: "{orange.100}",
            200: "{orange.200}",
            300: "{orange.300}",
            400: "{orange.400}",
            500: "{orange.500}",
            600: "{orange.600}",
            700: "{orange.700}",
            800: "{orange.800}",
            900: "{orange.900}",
            950: "{orange.950}",
        },
        secondary: {
            50: "{blue.50}",
            100: "{blue.100}",
            200: "{blue.200}",
            300: "{blue.300}",
            400: "{blue.400}",
            500: "{blue.500}",
            600: "{blue.600}",
            700: "{blue.700}",
            800: "{blue.800}",
            900: "{blue.900}",
            950: "{blue.950}",
        },
    },
    components: {
        tag: {
            colorScheme: {
                light: {
                    info: {
                        background: "{secondary.400}",
                        color: "white",
                    },
                },
            },
        },
        accordion: SQUARE_ACCORDION,
        fieldset: SQUARE_FIELDSET,
    },
});

/** High-contrast (black/white) accessibility variant of the persolog theme. */
export const persologHighContrast = definePreset(Aura, {
    primitive: {
        slate: {
            50: "#ffffff",
            100: "#ffffff",
            200: "#ffffff",
            300: "#ffffff",
            400: "#ffffff",
            500: "#000000",
            600: "#000000",
            700: "#000000",
            800: "#000000",
            900: "#000000",
            950: "#000000",
        },
    },
    semantic: {
        primary: {
            50: "#ffffff",
            100: "#ffffff",
            200: "#ffffff",
            300: "#ffffff",
            400: "#ffffff",
            500: "#000000",
            600: "#000000",
            700: "#000000",
            800: "#000000",
            900: "#000000",
            950: "#000000",
        },
        secondary: {
            50: "#ffffff",
            100: "#ffffff",
            200: "#ffffff",
            300: "#ffffff",
            400: "#ffffff",
            500: "#000000",
            600: "#000000",
            700: "#000000",
            800: "#000000",
            900: "#000000",
            950: "#000000",
        },
        colorScheme: {
            light: {
                surface: {
                    50: "#ffffff",
                    100: "#ffffff",
                    200: "#ffffff",
                    300: "#ffffff",
                    400: "#ffffff",
                    500: "#000000",
                    600: "#000000",
                    700: "#000000",
                    800: "#000000",
                    900: "#000000",
                    950: "#000000",
                },
            },
        },
    },
    components: {
        tag: {
            colorScheme: {
                light: {
                    info: {
                        background: "{primary.500}",
                        color: "white",
                    },
                },
            },
        },
        accordion: SQUARE_ACCORDION,
        fieldset: SQUARE_FIELDSET,
    },
});

export { Aura };
